import React, { useEffect, useState } from 'react';
import { getMetricsForMonth } from '../../../services/apiServices';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer,
  Label,
} from 'recharts';

const DeveloperPrChart = () => {
  const [chartData, setChartData] = useState([]);
  const [developers, setDevelopers] = useState([]);
  const [activeDevelopers, setActiveDevelopers] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const loggedInUserDetails = JSON.parse(localStorage.getItem('loggedInUserDetails'));
        const rawData = await getMetricsForMonth(loggedInUserDetails);
        const formatted = formatChartData(rawData);
        setChartData(formatted.chartData);
        setDevelopers(formatted.developers);
        setActiveDevelopers(formatted.developers);
      } catch (error) {
        console.error('Error fetching metrics data:', error);
      }
    };
    fetchData();
  }, []);

  const toggleDeveloper = (devName) => {
    setActiveDevelopers((prev) =>
      prev.includes(devName)
        ? prev.filter((name) => name !== devName)
        : [...prev, devName]
    );
  };

  return (
    <div
      style={{
        margin: '40px 30px',
        border: '1px solid #ccc',
        padding: '40px 40px 60px 40px',
        borderRadius: 8,
        backgroundColor: '#f9f9f9',
        textAlign: 'center',
        minHeight: '1100px',
      }}
    >
      <h4 style={{ marginBottom: '20px' }}>Developer Month Wise PRs</h4>
      <ResponsiveContainer width="100%" height={450}>
        <LineChart
          data={chartData}
          margin={{ top: 40, right: 30, left: 20, bottom: 0 }}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis
            dataKey="month"
            tick={{ angle: 90, dx: 10, dy: 50 }}
            interval={0}
            height={100}
          >
            <Label
              value="Month"
              position="insideBottom"
              offset={70}
              fontWeight="bold"
            />
          </XAxis>
          <YAxis>
            <Label
              angle={270}
              position="centerBottom"
              value="Total PRs"
              style={{ textAnchor: 'middle', fontSize: '100%', fill: 'black' }}
            />
          </YAxis>
          <Tooltip
            content={({ active, payload, label }) => {
              if (active && payload && payload.length) {
                const filtered = payload
                  .filter((p) => p.value > 0)
                  .sort((a, b) => b.value - a.value);
                if (filtered.length === 0) return null;

                return (
                  <div
                    style={{
                      background: 'white',
                      border: '1px solid #ccc',
                      padding: 10,
                    }}
                  >
                    <p style={{ margin: 0 }}>
                      <strong>{label}</strong>
                    </p>
                    {filtered.map((entry) => (
                      <p
                        key={`tooltip-${entry.name}`}
                        style={{ color: entry.color, margin: 0 }}
                      >
                        {entry.name}: {entry.value}
                      </p>
                    ))}
                  </div>
                );
              }
              return null;
            }}
          />
          {developers.map((dev, index) =>
            activeDevelopers.includes(dev) ? (
              <Line
                key={dev}
                type="monotone"
                dataKey={dev}
                stroke={`hsl(${index * 60}, 70%, 50%)`}
                activeDot={{ r: 6 }}
              />
            ) : null
          )}
        </LineChart>
      </ResponsiveContainer>

      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'center',
          marginTop: 40,
        }}
      >
        {developers.map((dev, index) => {
          const isActive = activeDevelopers.includes(dev);
          return (
            <div
              key={`legend-${dev}`}
              onClick={() => toggleDeveloper(dev)}
              style={{
                margin: '0 10px 8px 10px',
                cursor: 'pointer',
                fontWeight: isActive ? 'bold' : 'normal',
                opacity: isActive ? 1 : 0.4,
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <span
                style={{
                  display: 'inline-block',
                  width: 10,
                  height: 10,
                  backgroundColor: `hsl(${index * 60}, 70%, 50%)`,
                  marginRight: 5,
                  borderRadius: 2,
                }}
              ></span>
              {dev}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const formatChartData = (rawData) => {
  const grouped = {};

  rawData.forEach((entry) => {
    const month = formatMonth(entry.month);
    const dev = entry.developer;
    const value = parseInt(entry.totalmergedprs || 0);

    if (!grouped[month]) grouped[month] = { month };
    grouped[month][dev] = (grouped[month][dev] || 0) + value;
  });

  const chartData = Object.values(grouped).sort(
    (a, b) => new Date(`01 ${a.month}`) - new Date(`01 ${b.month}`)
  );

  const allDevelopers = [...new Set(rawData.map((d) => d.developer))];

  chartData.forEach((row) => {
    allDevelopers.forEach((dev) => {
      if (!row[dev]) row[dev] = 0;
    });
  });

  return { chartData, developers: allDevelopers };
};

const formatMonth = (monthStr) => {
  const [year, month] = monthStr.split('-');
  const date = new Date(`${year}-${month}-01`);
  return date.toLocaleString('default', { month: 'short', year: 'numeric' });
};

export default DeveloperPrChart;
